// Modal / drawer / alertdialog primitives. Implements:
//   - focus trap (Tab cycles within; Shift+Tab cycles backward)
//   - ESC closes with dirty-form guard hook
//   - scrim click closes for non-form dialogs only
//   - role=dialog | role=alertdialog per use
//
// Per frontend-design §6: a single modal stack manager renders modals;
// only the top-most has the focus trap.

import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]):not([disabled]), [contenteditable]:not([contenteditable="false"])';

interface BaseModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  // 'dialog' for forms / one-off pickers; 'alertdialog' for confirmation
  // and blocking-error modals (per ARIA pattern).
  role?: 'dialog' | 'alertdialog';
  // If true, scrim click and ESC will not close (used for blocking
  // error modals and dirty-form drawers).
  scrimDismissDisabled?: boolean;
  // Callback that returns true if it's safe to close (for dirty-form
  // guard). If it returns false, ESC / scrim are no-ops.
  canClose?: () => boolean;
  // If true, suppress the header close (×) button. Used for
  // non-dismissible alertdialogs whose only path forward is an
  // explicit primary action (e.g., the re-auth modal whose sole
  // affordance is "Sign in").
  hideCloseButton?: boolean;
  children: ReactNode;
  describedById?: string;
  // Modal width preset. Drawer = right-side panel; modal = centered.
  variant?: 'modal' | 'drawer';
  // Optional className appended to the dialog container.
  contentClassName?: string;
}

export function Modal({
  open,
  onClose,
  title,
  role = 'dialog',
  scrimDismissDisabled = false,
  canClose,
  hideCloseButton = false,
  children,
  describedById,
  variant = 'modal',
  contentClassName,
}: BaseModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2, 9)}`);

  // Capture the previously-focused element so we can return focus on close.
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement as HTMLElement | null;
      // Move focus to the first focusable item inside the dialog.
      // Defer to end of microtask to allow rendering.
      window.queueMicrotask(() => {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        const first = focusables[0];
        if (first) first.focus();
        else root.focus();
      });
    } else if (triggerRef.current) {
      // On close, return focus to the trigger.
      try {
        triggerRef.current.focus();
      } catch {
        /* element may have unmounted */
      }
      triggerRef.current = null;
    }
  }, [open]);

  // ESC + Tab focus-trap.
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!open) return;
      if (e.key === 'Escape') {
        if (scrimDismissDisabled) return;
        if (canClose && !canClose()) return;
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = Array.from(
          root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => el.offsetParent !== null || el === document.activeElement);
        if (focusables.length === 0) {
          e.preventDefault();
          root.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [canClose, onClose, open, scrimDismissDisabled],
  );

  // Scrim click — non-alertdialog only and only if not disabled.
  const onScrimClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (role === 'alertdialog') return;
      if (scrimDismissDisabled) return;
      if (canClose && !canClose()) return;
      onClose();
    },
    [canClose, onClose, role, scrimDismissDisabled],
  );

  if (!open) return null;
  return (
    <div
      className={`modal-scrim modal-scrim-${variant}`}
      onClick={onScrimClick}
      onKeyDown={onKeyDown}
    >
      <div
        ref={dialogRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId.current}
        aria-describedby={describedById}
        className={`modal-content modal-content-${variant}${
          contentClassName ? ' ' + contentClassName : ''
        }`}
        tabIndex={-1}
      >
        <header className="modal-header">
          <h2 id={titleId.current} className="modal-title">
            {title}
          </h2>
          {hideCloseButton ? null : (
            <button
              type="button"
              className="modal-close"
              aria-label="Close"
              onClick={() => {
                if (canClose && !canClose()) return;
                onClose();
              }}
            >
              ×
            </button>
          )}
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
